import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  sendSearchAnalyticsEvent,
  sendViewSearchResultsAnalyticsEvent,
  useForceUpdate,
} from '@mb/lib';
import { getUserLang, isEuEnv } from '@mb/lib/i18n';
import ClickAwayListener from '@mui/base/ClickAwayListener';
import { Brand } from 'code/MIT/HomeProduct/view/frontend/pages/BrandsPage/redux/sliceCreators/brandsSlice';
import {
  ResultRow,
  AutocompleteItemType,
  ResultItem,
} from 'components/components/SearchAutocompleteResults/SearchAutocompleteResults.types';
import debounce from 'lodash/debounce';
import ReactDOM from 'react-dom';

import * as Styled from './AutocompleteResultsContainer.styles';
import { searchActionsEnum } from '../../../../pages/ProductSearch/config/constants';
import { sendAnalytics } from '../../api/analytics';
import {
  clearHistoryByEmail,
  deleteHistoryItem,
  fetchSuggestionsByQuery,
  fetchSuggestionsItems,
} from '../../api/autocomplete';
import { siteId, langGlobal } from '../../config';
import {
  getIsBrands,
  getResults,
  getShouldResultsOpen,
  getShouldShowHistory,
  getShouldShowImages,
  shouldRedirectToSearch,
  BRANDS_PATH,
  getEmail,
} from '../../helpers';
import {
  getQuerySelector,
  getSuggestionsSelector,
  getIsOpenSelector,
  getHistorySelector,
  getIsLoadingSelector,
  getDefaultSuggestionsSelector,
  getSuggestedItemsSelector,
  currentItemIndexSelector,
  getShadowQuerySelector,
  hoveredItemSelector,
  currentProductIndexSelector,
  itemsQuerySelector,
  xRequestIdSelector,
  currentQuickFiltersSelector,
} from '../../redux/selectors/autocompleteSelector';
import {
  setCurrentItemIndex,
  setCurrentProductIndex,
  setHoveredItem,
  setIsOpen,
  setQuery,
  setShadowQuery,
} from '../../redux/sliceCreators/autocompleteSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { ArrowEvents, RecommendationItem } from '../../types';

const didYouMeanLabel = 'didyoumean';

interface AutocompleteResultsContainerProps {
  searchContainerId?: string;
  autocompleteContainerId?: string;
  absoluteContainer?: React.ReactElement<{
    open?: boolean;
    topOffset?: number;
  }>;
  autocompleteWrapper?: React.ReactElement<{ searchFormRect?: DOMRect }>;
  backdrop?: React.ReactElement<{ open?: boolean }>;
  contentPadding?: string;
  borderRadius?: string;
  boxShadow?: string;
  onClose?: () => void;
}

const AutocompleteResultsContainer: React.FC<
  AutocompleteResultsContainerProps
> = ({
  searchContainerId,
  autocompleteContainerId,
  absoluteContainer,
  autocompleteWrapper,
  onClose,
  backdrop,
}) => {
  const query = useAppSelector(getQuerySelector);
  const shadowQuery = useAppSelector(getShadowQuerySelector);
  const itemsQuery = useAppSelector(itemsQuerySelector);
  const suggestionsList = useAppSelector(getSuggestionsSelector);
  const suggestedItems = useAppSelector(getSuggestedItemsSelector);
  const hoveredItem = useAppSelector(hoveredItemSelector);
  const searchHistory = useAppSelector(getHistorySelector);
  const isOpen = useAppSelector(getIsOpenSelector);
  const isLoading = useAppSelector(getIsLoadingSelector);
  const defaultSuggestions = useAppSelector(getDefaultSuggestionsSelector);
  const currentIndex = useAppSelector(currentItemIndexSelector);
  const currentProductIndex = useAppSelector(currentProductIndexSelector);
  const xRequestId = useAppSelector(xRequestIdSelector);
  const currentQuickFilter = useAppSelector(currentQuickFiltersSelector);
  const [brands, setBrands] = useState<Brand[]>([]);
  const isMobile = window.innerWidth < 768;
  const isBrands = window.location.pathname === BRANDS_PATH;
  const isHomepage = window.location.pathname?.includes('/home');
  const isLoggedIn = Boolean(getEmail());

  const shouldShowSuggestions = true;
  const shouldShowHistory = getShouldShowHistory(query, searchHistory);
  const shouldResultsOpen = getShouldResultsOpen(query);
  const shouldShowImages = getShouldShowImages(query, isLoading);
  const suggestedItemsToShow: RecommendationItem[] = suggestedItems;
  const shouldShowSuggestedItems =
    suggestedItemsToShow.length > 0 && !isMobile && !isBrands;
  const shouldOpen = isOpen && shouldResultsOpen;

  const { suggestions, correctionQuery } = useMemo(() => {
    if (!Array.isArray(suggestionsList?.items)) {
      return {
        suggestions: undefined,
        correctionQuery: undefined,
      };
    }
    const correctionQuery = suggestionsList?.items?.find(
      (item) => item.sourceType === didYouMeanLabel,
    );
    const suggestions = {
      ...suggestionsList,
      items: suggestionsList?.items?.filter(
        (item) => item.sourceType !== didYouMeanLabel,
      ),
    };
    return {
      correctionQuery,
      suggestions,
    };
  }, [suggestionsList]);

  const handleWindowResize = useCallback(
    debounce(() => {
      forceUpdate();
    }, 150),
    [],
  );

  useEffect(() => {
    if (shouldOpen) {
      window.addEventListener('resize', handleWindowResize);
      return () => window.removeEventListener('resize', handleWindowResize);
    }
    return;
  }, [shouldOpen, handleWindowResize]);

  useEffect(() => {
    document.addEventListener('searchItemsChange', (event: CustomEvent) => {
      setBrands(event.detail.brands ?? []);
    });
    return () => {
      document.removeEventListener('searchItemsChange', () => {});
    };
  }, []);

  const dispatch = useAppDispatch();
  const forceUpdate = useForceUpdate();

  const handleClickAway = (event: any) => {
    if (!isOpen) {
      return;
    }

    const isSearchFormClicked = event.target.closest(
      searchContainerId ? `#${searchContainerId}` : '#headerSearchForm',
    );
    const isQuickViewClicked = event.target.closest('#QuickViewModal');
    const isMiniPdpClicked = event.target.closest('#miniPdpV2Modal');

    if (!(isSearchFormClicked || isQuickViewClicked || isMiniPdpClicked)) {
      dispatch(setIsOpen(false));
      onClose?.();
      window.dispatchEvent(
        new CustomEvent('toggle-main-search', {
          detail: { show: false },
        }),
      );
    }
  };

  const handleClickAwayAutocompleteResults = (event: any) => {
    const isAutocompleteResultsClicked = event.target.closest(
      '#searchAutocompleteResults',
    );
    const isSuggestedItemsClicked = event.target.closest(
      '#searchSuggestedItemsWrapper',
    );

    if (!(isAutocompleteResultsClicked || isSuggestedItemsClicked)) {
      dispatch(setIsOpen(false));
      onClose?.();
      window.dispatchEvent(
        new CustomEvent('toggle-main-search', {
          detail: { show: false },
        }),
      );
    }
  };

  const handleItemHover = (
    value: string,
    index: number = 0,
    item: ResultItem,
  ) => {
    if (isBrands) return;
    dispatch(setCurrentProductIndex(undefined));
    dispatch(setHoveredItem(item));
    dispatch(fetchSuggestionsItems(value));
    if (index != null) {
      const isTrending = item?.type === 'trending';
      const searchHistoryLength = searchHistory?.length;
      const newIndex =
        query?.length < 3 && isTrending ? searchHistoryLength + index : index;
      dispatch(setCurrentItemIndex(newIndex));
    }
  };

  const debouncedHandleItemHover = debounce(handleItemHover, 250);

  const handleItemClick = (value: string, type: AutocompleteItemType) => {
    sessionStorage.setItem('ssScroll', '0');
    sessionStorage.setItem('prevSearchUrl', 'null');
    let suggesterType: undefined | string = undefined;
    let suggesterValue = value;
    let query = value;
    let historyQuery = null;

    window.scrollTo(0, 0);

    window.splitFactory
      ?.client()
      ?.track('user', 'mb_frontend_homepage_search', undefined, {
        search_query: value,
        type,
      });

    if (getIsBrands()) {
      dispatch(setQuery(value));
      const searchFormSubmit = new CustomEvent('searchFormSubmit', {
        detail: {
          query: value,
          action: searchActionsEnum.searchBar,
          searchType: 'Pure search',
        },
      });
      document.dispatchEvent(searchFormSubmit);
      dispatch(setIsOpen(false));
      return;
    }

    if (type === AutocompleteItemType.HISTORY) {
      const historyItem = searchHistory.find(
        (item: any) => item.value === value,
      );
      if (historyItem?.suggesterType != null) {
        suggesterType = historyItem?.suggesterType;
      }
      if (historyItem?.suggesterValue != null) {
        suggesterValue = historyItem?.suggesterValue;
      }
      query = historyItem?.value || '';
      historyQuery = historyItem?.value;
      dispatch(setQuery(historyQuery || ''));
    }

    handleSearch({
      query,
      suggesterType,
      suggesterValue,
      historyQuery,
      searchType: type,
    });
  };

  const handleClearHistory = () => {
    dispatch(setCurrentItemIndex(undefined));
    dispatch(setCurrentProductIndex(undefined));
    dispatch(clearHistoryByEmail());
    window.dispatchEvent(
      new CustomEvent('toggle-main-search', { detail: { show: false } }),
    );
  };

  const handleSearch = (args: any) => {
    const suggesterType = args.suggesterType
      ? `&suggesterType=${args.suggesterType}`
      : '';
    let suggesterValue = args.suggesterValue
      ? `&suggesterValue=${args.suggesterValue}`
      : '';
    const searchType = args.searchType ? `&searchType=${args.searchType}` : '';
    let newQuery = args?.historyQuery || query;
    // Fix for Defaults suggestions, which don't have suggesterType/suggesterValue
    const isDefaultSuggestion =
      !Boolean(args.suggesterType) && Boolean(args?.suggesterValue);
    const qf = currentQuickFilter;
    const filterValue: Record<string, string | boolean> = {};
    let filterUrlValue = '';

    if (qf?.field != null) {
      const afValue = qf.value?.[0] || qf.value;
      filterValue[`filter.${qf.field}`] = afValue === 'true' ? true : afValue;
      filterUrlValue = `#/filter:${qf.field}:${qf.value?.[0] || qf.value}#/`;
    }

    const storageLang = getUserLang();
    const searchUrlPrefix = !isEuEnv
      ? ''
      : storageLang != null
      ? `/${storageLang}`
      : '';

    if (shouldRedirectToSearch()) {
      if (isDefaultSuggestion) {
        suggesterValue = '';
        newQuery = args.suggesterValue;
      }
      window.location.assign(
        `${searchUrlPrefix}/search?q=${newQuery}${
          filterUrlValue || ''
        }${suggesterType}${suggesterValue}${searchType}`,
      );
      sendSearchAnalyticsEvent({
        query: newQuery,
        searchType: args.searchType,
      });
      return;
    }

    const stringId = String(args.query);
    if (getIsBrands()) {
      const brand = brands.find((brand) => brand.id === stringId);
      if (brand == null) {
        return;
      }
      const url = `${brand?.search_for_button}#/filterValueLimiter=true`;
      window.location.assign(url);
      return;
    } else {
      if (isDefaultSuggestion) {
        dispatch(setQuery(args.suggesterValue));
      }

      const query = isDefaultSuggestion ? args.suggesterValue : newQuery;
      const suggesterValue = isDefaultSuggestion ? null : args.suggesterValue;
      const searchFormSubmit = new CustomEvent('searchFormSubmit', {
        detail: {
          query,
          page: 1,
          action: 'searchBar',
          suggesterType: args.suggesterType,
          suggesterValue,
          searchType: args.searchType,
          additionalFilters: filterValue,
        },
      });
      document.dispatchEvent(searchFormSubmit);
      sendSearchAnalyticsEvent({
        query: query,
        searchType: args.searchType,
      });
    }
    dispatch(setIsOpen(false));
    onClose?.();
  };

  const handleNoResultsActionClick = () => {
    if (isBrands) {
      const storageLang = getUserLang();
      const searchUrlPrefix = !isEuEnv
        ? ''
        : storageLang != null
        ? `/${storageLang}`
        : '';
      window.location.assign(`${searchUrlPrefix}/search?q=${query}`);
      sendSearchAnalyticsEvent({ query: query });
    }
  };

  const autocompleteContainer = document.getElementById(
    autocompleteContainerId || 'headerMainContent',
  );
  const headerSearchForm = document.getElementById(
    searchContainerId || 'headerSearchForm',
  );

  const defaultResults = useMemo(() => {
    return query.length > 2
      ? suggestions
        ? [suggestions]
        : []
      : defaultSuggestions
      ? [defaultSuggestions].slice(0, 5)
      : [];
  }, [query, suggestions, defaultSuggestions]);

  const brandsResults: ResultRow[] = useMemo(() => {
    const mappedBrands = brands.map((brand) => ({
      value: brand.manufacturer_name,
      id: brand.id,
    }));

    const filteredBrands = mappedBrands.filter((brand) =>
      brand?.value?.toLowerCase().includes(query?.toLowerCase()),
    );

    const slicedBrands = filteredBrands.slice(0, 5);

    if (slicedBrands.length === 0) {
      return [];
    }

    return [
      {
        items: slicedBrands,
      },
    ];
  }, [brands, query]);

  useEffect(() => {
    if (!shouldShowSuggestedItems) return;

    window.addEventListener(ArrowEvents.Left, handleArrowLeft);
    window.addEventListener(ArrowEvents.Right, handleArrowRight);
    return () => {
      window.removeEventListener(ArrowEvents.Left, handleArrowLeft);
      window.removeEventListener(ArrowEvents.Right, handleArrowRight);
    };
  }, [
    currentIndex,
    suggestedItemsToShow,
    currentProductIndex,
    shouldShowSuggestedItems,
  ]);

  useEffect(() => {
    window.addEventListener(ArrowEvents.Up, handleArrowUp);
    window.addEventListener(ArrowEvents.Down, handleArrowDown);
    return () => {
      window.removeEventListener(ArrowEvents.Up, handleArrowUp);
      window.removeEventListener(ArrowEvents.Down, handleArrowDown);
    };
  }, [
    currentIndex,
    defaultResults,
    brandsResults,
    searchHistory,
    query,
    currentProductIndex,
  ]);

  const results = getResults(defaultResults, brandsResults);
  const resultItems =
    query?.length > 2
      ? results?.[0]?.items
      : [...searchHistory, ...(results?.[0]?.items || [])];

  const handleArrowUp = () => {
    if (currentProductIndex != null) {
      if (currentProductIndex - 3 < 0) {
        dispatch(setCurrentProductIndex(undefined));
        return;
      } else {
        dispatch(setCurrentProductIndex(currentProductIndex - 3));
      }
    } else {
      let newIndex = currentIndex;
      if (currentIndex === undefined || currentIndex === 0) {
        // reset if top end of list
        if (currentIndex === 0) {
          dispatch(setCurrentItemIndex(undefined));
          dispatch(setShadowQuery(undefined));
          dispatch(fetchSuggestionsByQuery(query));
        }
        return;
      } else {
        newIndex = currentIndex - 1;
      }
      const newValue = resultItems?.[newIndex]?.value;
      if (newValue != null) {
        dispatch(setShadowQuery(newValue));
        handleItemHover(newValue, newIndex, resultItems?.[newIndex]);
      }
      dispatch(setCurrentItemIndex(newIndex));
    }
  };

  const handleArrowDown = () => {
    if (currentProductIndex != null) {
      if (currentProductIndex + 3 > suggestedItems?.length - 1) {
        dispatch(setCurrentProductIndex(undefined));
        return;
      } else {
        dispatch(setCurrentProductIndex(currentProductIndex + 3));
      }
    } else {
      let newIndex = currentIndex;
      if (
        currentIndex === undefined ||
        currentIndex === resultItems?.length - 1
      ) {
        // reset if bottom end of list
        if (currentIndex === resultItems?.length - 1) {
          dispatch(setCurrentItemIndex(undefined));
          dispatch(setShadowQuery(undefined));
          dispatch(fetchSuggestionsByQuery(query));
          return;
        } else {
          newIndex = 0;
        }
      } else {
        newIndex = currentIndex + 1;
      }
      const newValue = resultItems?.[newIndex]?.value;
      if (newValue != null) {
        dispatch(setShadowQuery(newValue));
        handleItemHover(newValue, newIndex, resultItems?.[newIndex]);
      }
      dispatch(setCurrentItemIndex(newIndex));
    }
  };

  const handleArrowLeft = () => {
    if (currentIndex == null) {
      return;
    }
    let newIndex = currentProductIndex;
    if (currentProductIndex === 0 || currentProductIndex === 3) {
      dispatch(setCurrentProductIndex(undefined));
      return;
    }
    if (currentProductIndex === undefined) {
      newIndex = suggestedItemsToShow?.length - 1;
    } else {
      newIndex = currentProductIndex - 1;
    }
    dispatch(setCurrentProductIndex(newIndex));
  };

  const handleArrowRight = () => {
    if (currentIndex == null) {
      return;
    }

    let newIndex = currentProductIndex;
    if (
      currentProductIndex === 2 ||
      currentProductIndex === suggestedItemsToShow?.length - 1
    ) {
      dispatch(setCurrentProductIndex(undefined));
      return;
    }
    if (currentProductIndex === undefined) {
      newIndex = 0;
    } else {
      newIndex = currentProductIndex + 1;
    }
    dispatch(setCurrentProductIndex(newIndex));
  };

  const handleProductsWrapperMouseEnter = () => {
    dispatch(setCurrentProductIndex(undefined));
  };

  const handleQueryClick = (newValue: string) => {
    if (query !== newValue) {
      dispatch(setQuery(newValue));
      handleSearch({
        query: newValue,
        suggesterType: 'didyoumean',
        suggesterValue: correctionQuery?.value,
        historyQuery: null,
      });
    } else {
      handleSearch({
        query: newValue,
        historyQuery: null,
      });
    }
  };

  const handleProductClick = async (
    item: RecommendationItem,
    index: number,
  ) => {
    if (!isLoggedIn) {
      window.splitFactory
        ?.client()
        ?.track('user', 'mb_guest_search', undefined, {});
    }

    const actionPostFix =
      hoveredItem?.type != null ? `_${hoveredItem.type}` : '';
    await sendAnalytics({
      id: [item.id],
      type: 'view',
      siteId: siteId,
      action: `suggester${actionPostFix}`,
      customer: {
        email: getEmail(),
      },
      value: hoveredItem?.value || shadowQuery || query,
      lang: langGlobal,
      impressions: suggestedItemsToShow?.map((item) => item.id),
    });
    sendViewSearchResultsAnalyticsEvent({
      query: hoveredItem?.value || shadowQuery || query,
      searchType: 'product_thumbnail',
      resultsNumber: suggestedItemsToShow?.length || 0,
    });
    dispatch(setCurrentProductIndex(index));

    window.dispatchEvent(
      new CustomEvent('initMiniPDP', {
        detail: {
          id: item.variantId || item.id,
          clickedProductGridIndex: index,
          clickedProductRequestId: xRequestId,
        },
      }),
    );
    dispatch(setCurrentProductIndex(undefined));
    dispatch(setIsOpen(false));
  };

  const handleClearHistoryItem = (value: string) => {
    dispatch(deleteHistoryItem(value));
  };

  const showAction = isBrands;
  const noResultsPlace = isBrands ? 'Brands' : undefined;

  const AbsoluteContainer = React.isValidElement(absoluteContainer)
    ? () => absoluteContainer
    : absoluteContainer || Styled.AbsoluteContainer;
  const AutocompleteWrapper = React.isValidElement(autocompleteWrapper)
    ? () => autocompleteWrapper
    : autocompleteWrapper || Styled.ContentWrapper;
  const BackdropComponent = React.isValidElement(backdrop)
    ? () => backdrop
    : backdrop || Styled.Backdrop;

  const selectedItemId =
    currentIndex != undefined && currentProductIndex == null
      ? resultItems?.[currentIndex]?.id
      : undefined;

  return (
    <React.Fragment>
      {autocompleteContainer &&
        ReactDOM.createPortal(
          <React.Fragment>
            {ReactDOM.createPortal(
              <BackdropComponent open={shouldOpen} />,
              document.body,
            )}
            <ClickAwayListener onClickAway={handleClickAway}>
              <AbsoluteContainer
                open={shouldOpen}
                topOffset={
                  autocompleteContainer?.getBoundingClientRect().bottom
                }
                onClick={handleClickAwayAutocompleteResults}
              >
                <AutocompleteWrapper
                  searchFormRect={headerSearchForm?.getBoundingClientRect()}
                >
                  <Styled.AutocompleteResults
                    shouldShowClearItemButton
                    shouldSplitHalf={shouldShowSuggestedItems}
                    shouldShowArrows={false}
                    results={results}
                    isResultsOpen={shouldOpen}
                    withRowGap={false}
                    correctedQuery={correctionQuery?.value}
                    query={query}
                    onQueryClick={handleQueryClick}
                    onItemClick={handleItemClick}
                    onItemHover={debouncedHandleItemHover}
                    onClearItem={handleClearHistoryItem}
                    showAction={showAction}
                    onActionClick={handleNoResultsActionClick}
                    searchHistory={searchHistory}
                    shouldShowHistory={shouldShowHistory}
                    shouldShowSuggestions={shouldShowSuggestions}
                    noResultsPlace={noResultsPlace}
                    onClearHistory={handleClearHistory}
                    shouldShowImages={shouldShowImages}
                    shouldShowDividers={false}
                    shouldShowIcons
                    maxHistoryItemsCount={9}
                    maxMobileItemsCount={9}
                    invertQueryHighlighting
                    selectedItemId={selectedItemId}
                  />
                  {shouldShowSuggestedItems && (
                    <Styled.SuggestedItemsWrapper
                      id="searchSuggestedItemsWrapper"
                      onMouseEnter={handleProductsWrapperMouseEnter}
                    >
                      {(shadowQuery ?? query ?? itemsQuery)?.length > 0 && (
                        <div
                          className={`font-graphik font-semibold ${
                            isHomepage ? 'text-[14px]' : 'text-[18px]'
                          } mb-4`}
                        >
                          Results for &quot;{itemsQuery || query}&quot;
                        </div>
                      )}
                      <div className="grid grid-cols-3">
                        {suggestedItemsToShow.map((item, index) => (
                          <div
                            key={item.id}
                            data-selector="search-drawer-thumbnail"
                            className={`relative rounded-[20px] cursor-pointer p-2 ${
                              currentProductIndex === index
                                ? 'bg-gray-100'
                                : 'bg-white'
                            } hover:bg-gray-100`}
                            onClick={() => handleProductClick(item, index)}
                          >
                            <div className="w-full aspect-square overflow-hidden">
                              <img
                                src={item.imageUrl}
                                alt={item.name}
                                className="w-full h-full object-cover rounded-[20px]"
                                style={{ filter: 'brightness(0.95)' }}
                                onError={(e) => {
                                  e.currentTarget.src =
                                    'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs%3D';
                                }}
                              />
                            </div>
                            <div className="absolute bottom-4 left-4 right-4 flex flex-col items-start gap-1 max-w-full">
                              {item.categories?.[0] && (
                                <div className="bg-black bg-opacity-50 text-white text-[10px] leading-none px-2 py-1 rounded-full whitespace-nowrap overflow-hidden max-w-full truncate">
                                  {item.categories
                                    ?.at(-1)
                                    ?.split(/>|\//)
                                    ?.pop()}
                                </div>
                              )}
                              <div className="bg-black bg-opacity-50 text-white text-[10px] leading-none px-2 py-1 rounded-full whitespace-nowrap overflow-hidden max-w-full truncate">
                                {item.manufacturer}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Styled.SuggestedItemsWrapper>
                  )}
                </AutocompleteWrapper>
              </AbsoluteContainer>
            </ClickAwayListener>
          </React.Fragment>,
          autocompleteContainer,
        )}
    </React.Fragment>
  );
};

export default AutocompleteResultsContainer;
